/* global google, propel */

import { __ } from '@wordpress/i18n';
import sanitizeHTML from 'propel/sanitizeHTML';

/**
 * HeroMap class to handle map-related functionalities.
 */
class HeroMap {
	/**
	 * Initializes the Filters class with provided block and postsWrapper elements.
	 *
	 * @param {HTMLElement} mapElement            - The map element.
	 * @param {Object}      Map                   - The Google Maps Map class.
	 * @param {Object}      InfoWindow            - The Google Maps InfoWindow class.
	 * @param {Object}      AdvancedMarkerElement - The Google Maps AdvancedMarkerElement class.
	 * @param {Object}      PinElement            - The Google Maps PinElement class.
	 * @param {Object}      RenderingType         - The Google Maps RenderingType enum.
	 * @return {boolean} Returns false if the block element is falsy.
	 */
	constructor(
		mapElement,
		Map,
		InfoWindow,
		AdvancedMarkerElement,
		PinElement,
		RenderingType
	) {
		this.parser = new DOMParser();
		this.mapElement = mapElement;
		this.AdvancedMarkerElement = AdvancedMarkerElement;
		this.PinElement = PinElement;

		if (!this.mapElement) {
			return false;
		}

		const options = {
			center: {
				lat: 39.8283,
				lng: -98.5795,
			},
			zoom: 4,
			mapTypeControl: false,
			fullscreenControl: false,
			streetViewControl: false,
			scaleControl: false,
			zoomControl: true,
			mapId: '6ab47e49eb38c78cfb82aa3c',
			renderingType: RenderingType.VECTOR,
			allMarkers: {},
		};

		this.map = new Map(this.mapElement, options);
		this.markers = {};
		this.infoWindow = new InfoWindow({
			pixelOffset: new google.maps.Size(0, -4),
		});

		google.maps.event.addListener(this.infoWindow, 'domready', () => {
			const iwOuter = document.querySelector('.gm-style-iw');

			if (iwOuter) {
				iwOuter.addEventListener('click', (e) => {
					if (e.target.tagName && 'SUMMARY' === e.target.tagName) {
						this.infoWindow.open({
							anchor: this.infoWindow.anchor,
							map: this.map,
							shouldFocus: false,
						});
					}
				});
			}
		});

		this.addMarker();
		this.centerMap();

		setTimeout(() => {
			requestAnimationFrame(() => {
				this.mapElement.classList.add('block-hero-map__map--visible');
			});
		}, 200);
	}

	addMarker() {
		if (
			!this.mapElement?.dataset?.id ||
			!this.mapElement?.dataset?.lat ||
			!this.mapElement?.dataset?.lng
		) {
			return;
		}

		const locationId = this.mapElement.dataset.id;

		if (this.markers[locationId]) {
			this.markers[locationId].setMap(this.map);

			return this.markers[locationId];
		}

		const latLng = new google.maps.LatLng(
			this.mapElement.dataset.lat,
			this.mapElement.dataset.lng
		);

		const title = this.mapElement?.dataset?.title || '';
		const address = this.mapElement?.dataset?.address || '';
		const directions = this.mapElement?.dataset?.directions || '';
		const markerIcon = this.mapElement.querySelector(
			'.location-meta__set-location-button--selected'
		)
			? 'selected'
			: '';

		let markerHtml = `<div class="component-map-marker" aria-labelledby="location-marker-title"><h2 id="location-marker-title" class="component-map-marker__title">${title}</h2>`;

		if (address) {
			markerHtml += `<div class="component-map-marker__address">${address}</div>`;
		}

		if (directions) {
			markerHtml += `<div class="component-map-marker__directions wp-block-button wp-block-button--small"><a href="${sanitizeHTML(directions)}" target="_blank" rel="noopener noreferrer" class="wp-block-button__link">${__('Get Directions', 'propel')}</a></div>`;
		}

		markerHtml += '</div>';

		const markerContentElement = this.getMarkerContentElement(
			markerHtml,
			markerIcon,
			markerIcon
				? [
						'component-map-marker__icon',
						'component-map-marker__icon--selected',
					]
				: ['component-map-marker__icon']
		);

		const locationTitle = this.mapElement.getAttribute('data-title');

		const markerSettings = {
			position: latLng,
			map: this.map,
			title: `${__('View details for', 'propel')} ${locationTitle}`,
			content: markerContentElement,
		};

		const marker = new this.AdvancedMarkerElement(markerSettings);

		this.markers[locationId] = marker;

		marker.addListener(
			'click',
			this.openMarker.bind(this, marker, this.mapElement)
		);

		this.openMarker(marker);

		return marker;
	}

	getMarkerContentElement(markerHtml = '', markerIcon = '', classes = []) {
		const markerContentElement = document.createElement('div');
		markerContentElement.setAttribute(
			'data-content',
			sanitizeHTML(markerHtml)
		);
		markerContentElement.appendChild(this.getMarkerIconElement(markerIcon));

		if (typeof classes === 'string') {
			classes = [classes];
		}

		classes.forEach((className) => {
			markerContentElement.classList.add(className);
		});

		return markerContentElement;
	}

	getMarkerIconElement(markerIcon = '') {
		if (propel?.customMarkerIcon) {
			markerIcon = propel.customMarkerIcon;
		} else {
			markerIcon = `<svg width="31" height="38" fill="none" version="1.1" viewBox="0 0 31 38" xmlns="http://www.w3.org/2000/svg"><g transform="translate(-8.5,-4.5)" filter="url(#filter0_d_33499_168150)"><path d="m24 5.7571c7.9496 0 14.5 6.0176 14.5 13.6 0 3.4458-1.6348 7.3162-3.6768 10.841-2.0601 3.5559-4.6199 6.9013-6.6377 9.3321-2.2046 2.6559-6.1664 2.6559-8.371 0-2.0178-2.4308-4.5776-5.7762-6.6377-9.3321-2.042-3.5246-3.6768-7.395-3.6768-10.841 2.3e-4 -7.582 6.5504-13.6 14.5-13.6zm0 10.1c-2.1751 0-3.8211 1.6323-3.8213 3.5 0 1.8679 1.6461 3.5 3.8213 3.5s3.8213-1.6321 3.8213-3.5c-2e-4 -1.8677-1.6462-3.5-3.8213-3.5z" fill="var(--button-primary-light-default, #777)" stroke="#fff" stroke-width="2"/></g><defs><filter id="filter0_d_33499_168150" x="0" y="0" width="48" height="55" color-interpolation-filters="sRGB" filterUnits="userSpaceOnUse"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feColorMatrix in="SourceAlpha" result="hardAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"/><feOffset dy="4"/><feGaussianBlur stdDeviation="4"/><feComposite in2="hardAlpha" operator="out"/><feColorMatrix values="0 0 0 0 0.0392157 0 0 0 0 0.0392157 0 0 0 0 0.0392157 0 0 0 0.06 0"/><feBlend in2="BackgroundImageFix" result="effect1_dropShadow_33499_168150"/><feBlend in="SourceGraphic" in2="effect1_dropShadow_33499_168150" result="shape"/></filter></defs></svg>`;
		}

		return this.parser.parseFromString(markerIcon, 'image/svg+xml')
			.documentElement;
	}

	openMarker(marker) {
		this.infoWindow.close();
		this.infoWindow.setContent(marker.content.getAttribute('data-content'));
		this.infoWindow.open({
			anchor: marker,
			map: this.map,
			shouldFocus: false,
		});
	}

	centerMap() {
		const bounds = new google.maps.LatLngBounds();
		let markerCount = 0;

		Object.values(this.markers).forEach((marker) => {
			if (!marker.map || !marker.position) {
				return;
			}
			bounds.extend(marker.position);
			markerCount++;
		});

		if (markerCount === 1) {
			this.map.setCenter(bounds.getCenter());
			this.map.setZoom(16);
		} else if (!bounds.isEmpty()) {
			this.map.fitBounds(bounds, 60);
		}

		this.mapMoved = false;
	}
}

async function initMap() {
	const urlParams = new URLSearchParams(window.location.search);

	if (urlParams.has('no-hero-map')) {
		return;
	}

	const mapElements = document.querySelectorAll('.block-hero-map__map');

	if (0 === mapElements.length) {
		return;
	}

	// eslint-disable-next-line no-unused-vars
	const { Map, InfoWindow, RenderingType } =
		await google.maps.importLibrary('maps');

	// eslint-disable-next-line no-unused-vars
	const { AdvancedMarkerElement, PinElement } =
		await google.maps.importLibrary('marker');

	mapElements.forEach((mapElement) => {
		new HeroMap(
			mapElement,
			Map,
			InfoWindow,
			AdvancedMarkerElement,
			PinElement,
			RenderingType
		);
	});
}

if (window?.google?.maps) {
	initMap();
} else {
	window.addEventListener('google-maps-loaded', initMap);
}
